import type BugsnagStatic from '@bugsnag/js';
import type { Event } from '@bugsnag/js';

type BugsnagClient = typeof BugsnagStatic;
type NotifyArgs = Parameters<BugsnagClient['notify']>;

const IGNORED_ERRORS: { message: string | RegExp }[] = [
  // Supports exact string match: { message: 'Some error' }
  // Supports regex match: { message: /pattern/i }
  { message: 'Cancel rendering route' },
  // Transient network errors on iOS Safari / Chrome — not actionable
  { message: /load failed/i },
  { message: /failed to fetch/i },
  { message: /^timeout on /i }
];

function shouldIgnoreError(event: Event) {
  // Check cancelled flag (Next.js navigation)
  if (event.originalError?.cancelled) {
    return true;
  }

  // Collect all error messages in the chain (main error + caused-by entries)
  const errorMessages = [
    ...(event.errors || []).map(e => e.errorMessage || ''),
    event.originalError?.message || ''
  ].filter(Boolean);

  return IGNORED_ERRORS.some(({ message }) =>
    errorMessages.some(msg => {
      if (message instanceof RegExp) return message.test(msg);
      return msg === message;
    })
  );
}

const isBrowser = typeof window !== 'undefined';
const isServer = !isBrowser;

const STAGING_DOMAINS = [
  '.azstaging.net',
  '.azstage.in',
  '.digit-az.app',
  '.vercel.app'
];

const matchesStagingDomain = (url?: string) =>
  STAGING_DOMAINS.some(domain => url?.includes(domain));

const getReleaseStage = () => {
  if (isBrowser) {
    const { hostname } = window.location;

    if (hostname.match(/^(?:.+\.)?bina\.az$/)) {
      return 'production';
    }

    if (matchesStagingDomain(hostname)) {
      return process.env.NEXT_PUBLIC_VERCEL_GITHUB_BRANCH || 'staging';
    }

    if (
      hostname === 'localhost' ||
      hostname === '127.0.0.1' ||
      hostname === '0.0.0.0'
    ) {
      return 'local';
    }

    return 'local';
  }

  if (process.env.VERCEL_ENV === 'production') {
    return 'production';
  }

  if (matchesStagingDomain(process.env.VERCEL_URL)) {
    return (
      process.env.VERCEL_GITHUB_BRANCH ||
      process.env.VERCEL_GIT_COMMIT_REF ||
      'staging'
    );
  }

  if (process.env.VERCEL_GITHUB_BRANCH) {
    return process.env.VERCEL_GITHUB_BRANCH;
  }

  return 'local';
};

const releaseStage = getReleaseStage();
const apiKey = process.env.BUGSNAG_API_KEY || 'DO_NOT_SEND';

const commonErrorSettings = {
  autoDetectErrors: true,
  enabledErrorTypes: {
    unhandledExceptions: true,
    unhandledRejections: true
  }
};

function buildConfig(plugins: unknown[] = []) {
  return {
    apiKey,
    appVersion: process.env.APP_VERSION,
    releaseStage,
    enabledReleaseStages: null,
    logger: null,
    ...commonErrorSettings,
    ...(isServer && {
      maxBreadcrumbs: 10,
      maxEvents: 1
    }),
    ...(isBrowser && {
      consoleBreadcrumbsEnabled: true,
      collectUserIp: false
    }),
    onError(event: Event) {
      if (shouldIgnoreError(event)) {
        return false;
      }

      if (apiKey === 'DO_NOT_SEND') {
        if (
          !process.env.NEXT_PUBLIC_E2E &&
          (releaseStage === 'local' || process.env.NODE_ENV === 'development')
        ) {
          const context = isBrowser ? 'Browser' : 'Server';
          console.log(
            `[Bugsnag ${context}] Error not sent (API key not configured):`,
            {
              releaseStage: event.app.releaseStage,
              error: event.errors?.[0]?.errorMessage,
              context: event.context
            }
          );
        }
        return false;
      }

      if (
        !process.env.NEXT_PUBLIC_E2E &&
        (releaseStage === 'local' || process.env.NODE_ENV === 'development')
      ) {
        const context = isBrowser ? 'Browser' : 'Server';
        console.log(`[Bugsnag ${context}] Sending error to Bugsnag:`, {
          releaseStage: event.app.releaseStage,
          error: event.errors?.[0]?.errorMessage,
          context: event.context
        });
      }

      return true;
    },
    plugins
  };
}

function logInitialized() {
  if (
    !process.env.NEXT_PUBLIC_E2E &&
    (releaseStage === 'local' || process.env.NODE_ENV === 'development')
  ) {
    const context = isBrowser ? 'Browser' : 'Server';
    console.log(
      `[Bugsnag ${context}] Initialized (releaseStage: ${releaseStage})`
    );
  }
}

function startBugsnagSync(): BugsnagClient {
  // Server path — kept sync. Next.js DCE strips this branch from the client bundle.
  // eslint-disable-next-line global-require
  const Bugsnag = require('@bugsnag/js').default as BugsnagClient;
  // Config shape matches previous eager init; Bugsnag typings are stricter than runtime.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  Bugsnag.start(buildConfig() as any);
  logInitialized();
  return Bugsnag;
}

let browserClient: BugsnagClient | null = null;
let browserLoadPromise: Promise<BugsnagClient> | null = null;
const pendingNotifies: NotifyArgs[] = [];

async function loadBrowserBugsnag(): Promise<BugsnagClient> {
  if (browserClient) {
    return browserClient;
  }

  if (!browserLoadPromise) {
    browserLoadPromise = (async () => {
      const [{ default: Bugsnag }, { default: BugsnagPluginReact }] =
        await Promise.all([
          import('@bugsnag/js'),
          import('@bugsnag/plugin-react')
        ]);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Bugsnag.start(buildConfig([new BugsnagPluginReact()]) as any);
      logInitialized();
      browserClient = Bugsnag;

      pendingNotifies.splice(0).forEach(args => {
        Bugsnag.notify(...args);
      });

      return Bugsnag;
    })();
  }

  return browserLoadPromise;
}

function scheduleBrowserBugsnagLoad() {
  // Intentionally empty: Bugsnag loads only when notify()/getPlugin()/start()
  // is called, so Lighthouse interaction simulation does not pull it in.
}

type BugsnagFacade = Pick<BugsnagClient, 'notify' | 'getPlugin'> & {
  start: BugsnagClient['start'];
};

function createBrowserBugsnagFacade(): BugsnagFacade {
  scheduleBrowserBugsnagLoad();

  return {
    notify(...args: NotifyArgs) {
      if (browserClient) {
        return browserClient.notify(...args);
      }

      pendingNotifies.push(args);
      loadBrowserBugsnag().catch(() => undefined);
      return undefined;
    },
    getPlugin(name) {
      if (!browserClient) {
        loadBrowserBugsnag().catch(() => undefined);
        return undefined;
      }
      return browserClient.getPlugin(name);
    },
    start() {
      // Started lazily on the client — keep API compatible with callers.
      loadBrowserBugsnag().catch(() => undefined);
      return undefined as unknown as ReturnType<BugsnagClient['start']>;
    }
  };
}

// Next.js replaces `typeof window === 'undefined'` at build time so the unused
// branch is eliminated — Bugsnag stays out of the critical client JS path.
const Bugsnag: BugsnagFacade =
  typeof window === 'undefined'
    ? startBugsnagSync()
    : createBrowserBugsnagFacade();

export default Bugsnag;
